import HCaptcha from '@hcaptcha/react-hcaptcha';
import {useRef, useState} from 'react';
import {FormProvider} from 'react-hook-form';
import {Link} from 'react-router';

import {FormIds} from '@/lib/forms/form-ids';
import {updatePasswordFormFields, updatePasswordFormSteps} from '@/lib/forms/update-password-form-config';
import {getBaseURL} from '@/lib/helpers/urlHelpers';

import {useAuth} from '@/providers/components/authProvider';
import {useAppForm} from '@/providers/components/formProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import FormFields from '@/components/form/FormFields';
import FormGlobalError from '@/components/form/FormGlobalError';
import FormSubmitButton from '@/components/form/FormSubmitButton';

type UpdatePasswordFormDataT = {
  password: string;
  confirmPassword: string;
  captchaToken: string;
};

function UpdatePasswordPage() {
  const {toast} = useNotifications();

  const {handleSignOut, handleUpdatePassword} = useAuth();

  // State
  const [captchaToken, setCaptchaToken] = useState<string | undefined>();

  // Refs
  const captchaRef = useRef<HCaptcha | null>(null);

  // Create the form in the form provider
  const {
    formConfig,
    setGlobalError,
    setLoading,
    // state: {loading},
  } = useAppForm<UpdatePasswordFormDataT>({
    formId: FormIds.UPDATE_PASSWORD,
    config: {
      fields: updatePasswordFormFields,
      steps: updatePasswordFormSteps,
    },
  });

  // React Hook Form Config
  const {
    handleSubmit,
    trigger,
    formState: {errors},
    reset,
  } = formConfig;

  // Submit handler
  const onSubmit = async ({password}: UpdatePasswordFormDataT) => {
    if (Object.keys(errors).length) {
      setGlobalError('Something went wrong. Please check the form for errors');
      trigger();
      return;
    }

    if (!captchaToken) {
      setGlobalError('Please complete the CAPTCHA verification');
      return;
    } else {
      setGlobalError('');
    }

    try {
      setLoading(true);
      await handleUpdatePassword(password, captchaToken);
      toast.success('Password updated successfully');
      reset();
    } catch (error) {
      setGlobalError((error as Error).message ?? 'Something went wrong. Please try again later.');
      reset({password});
    } finally {
      captchaRef.current?.resetCaptcha();
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="authentication authentication-basic h-full items-center text-defaultsize text-defaulttextcolor">
        <div className="mx-auto max-w-lg">
          <div className="">
            <div className="my-[2.5rem] flex justify-center">
              <Link to={getBaseURL()} tabIndex={-1}>
                <img src={'/images/logo/logo.png'} alt="logo" className="desktop-logo max-w-[150px]" />
                <img src={'/images/logo/logo.png'} alt="logo" className="desktop-dark max-w-[150px]" />
              </Link>
            </div>
            <div className="box">
              <div className="box-body !p-[3rem]">
                <h1 className="h3 mb-2 text-center font-semibold md:h1" id="signup-heading">
                  Update Password
                </h1>

                <small className="mb-5 block text-center text-gray-800/70 dark:text-white/40" id="signup-terms-text">
                  By resetting your password you are agreeing to our{' '}
                  <Link
                    to="https://tos.tnc.exchange/"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Terms and Conditions (opens in new tab)"
                  >
                    <u>Terms &amp; Conditions</u>
                  </Link>
                  <span className="mx-1">and</span>
                  <Link
                    to="https://pp.tnc.exchange/"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Privacy Policy (opens in new tab)"
                  >
                    <u>Privacy Policy</u>
                  </Link>
                  .
                </small>

                <FormProvider {...formConfig}>
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="flex flex-col gap-4"
                    aria-labelledby="signup-heading"
                    role="form"
                  >
                    <FormGlobalError formId={FormIds.UPDATE_PASSWORD} />
                    <FormFields formId={FormIds.UPDATE_PASSWORD} />
                    <div className="flex justify-center">
                      <HCaptcha
                        ref={captchaRef}
                        sitekey={import.meta.env.VITE_HCAPTCHA_SITE_KEY}
                        onVerify={token => {
                          setCaptchaToken(token);
                        }}
                        onExpire={() => {
                          setCaptchaToken(undefined);
                        }}
                      />
                    </div>
                    <FormSubmitButton formId={FormIds.UPDATE_PASSWORD} label="Update Password" showStepCount={false} />
                  </form>
                </FormProvider>

                <div className="text-center">
                  <p className="mt-4 text-[0.75rem] text-[#8c9097] dark:text-white/50">
                    Want to stop?{' '}
                    <button onClick={handleSignOut} className="text-primary">
                      Sign Out
                    </button>
                  </p>
                </div>
              </div>
            </div>
            <div className="mb-4 flex items-center justify-center gap-1">
              <Link
                to="https://tos.tnc.exchange"
                className="text-success"
                aria-label="View Terms and Conditions"
                target="_blank"
                rel="noreferrer"
              >
                <u>Terms &amp; Conditions</u>
              </Link>
              <span>|</span>
              <Link
                to="https://pp.tnc.exchange"
                className="text-success"
                aria-label="View Privacy Policy"
                target="_blank"
                rel="noreferrer"
              >
                <u>Privacy Policy</u>
              </Link>
            </div>
            <small className="my-5 block text-center text-gray-800/70">
              By creating an account you agree to our
              <br />
              Terms &amp; Conditions and Privacy Policy
            </small>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UpdatePasswordPage;
